/**
 * Section « Social » (D§3, D§4) — le partage.
 *
 * Elle n'existait pas : l'entrée du dock était là, la section n'était jamais montée, et
 * taper « Social » n'affichait rien. C'est la moitié du concept qui manquait à l'écran
 * (§9) — un idle joli et anonyme n'a aucune raison de circuler.
 *
 * ── Ce que ce panneau doit faire, et dans cet ordre ──────────────────────────
 *
 * 1. **Montrer le lien**, en entier et lisible. Un lien qu'on ne voit pas ne se partage
 *    pas de confiance : le joueur doit reconnaître le nom de sa ville dedans.
 * 2. **Un seul geste pour l'envoyer** — la feuille native sur téléphone, le
 *    presse-papiers ailleurs. C'est `shareCity` qui choisit, et qui dit ce qui a servi.
 * 3. **Dire ce qui s'est passé.** « Copié » quand c'est copié, rien quand le joueur a
 *    annulé, et une invitation à sélectionner le lien quand aucun chemin n'a marché.
 * 4. **Expliquer la contrepartie** : chaque visite unique booste la production. Sans
 *    cette phrase, le bouton n'est qu'un bouton de partage de plus.
 *
 * ── L'état d'attente n'est pas une erreur ────────────────────────────────────
 *
 * Le lien vient du serveur : le slug est généré par Postgres et le client n'a pas le
 * privilège de l'écrire (§5). Il n'existe donc pas pendant les premières secondes, et
 * jamais du tout si Supabase n'est pas configuré. D§4 impose qu'un panneau vide « raconte
 * quelque chose de chaleureux et propose une action » — on montre donc la ville en train
 * de se réserver son adresse, pas un espace mort ni un message d'erreur.
 */

import { VISITOR_DECORATIONS } from '@/config/balance';
import { formatClock } from '@/core/format';
import type { CitySnapshot } from '@/core/gameState';
import { n, t } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import { fetchSocialCounts } from '@/net/socialStats';
import type { RemoteCity } from '@/net/sync';
import { cityUrl, shareCity } from '@/viral/share';
import { createButton, createEmptyState, createPanel, createProgress } from './components';
import type { ButtonHandle, ProgressHandle } from './components';
import { SOCIAL_REWARD_GEMS } from '@/core/socialRewards';
import type { SocialChannel } from '@/core/socialRewards';
import { icon } from './icons';

export interface SocialSectionOptions {
  /** Origine des liens partagés. Injectée pour rester testable hors navigateur. */
  readonly origin: string;
  /** Retour au joueur : le même canal que le reste du jeu (toast). */
  readonly onNotice: (message: string) => void;
  /**
   * Le joueur vient d'ouvrir un réseau (ROADMAP 2.6). Rend les gemmes réellement
   * octroyées — **zéro si le réseau avait déjà été récompensé**.
   *
   * La section ne décide ni du montant ni de l'idempotence : elle ne connaît pas l'état de
   * jeu. Elle demande, et affiche ce qu'on lui rend. Optionnel, pour qu'elle reste montable
   * seule dans un test.
   */
  readonly onSocialVisit?: (channel: SocialChannel) => number;
  /** Réseaux déjà récompensés — pour marquer les tuiles sans les rendre inertes. */
  readonly claimedSocial?: () => readonly SocialChannel[];
  /**
   * Le lien vient d'être partagé — feuille native ou presse-papiers (ROADMAP 6.2).
   *
   * La section ne décide pas ce qu'on en fait : c'est le jeu qui sait s'il faut proposer
   * un compte, et à quelles conditions. Optionnel, pour que la section reste montable
   * seule dans un test.
   */
  readonly onShared?: () => void;
}

export interface SocialSectionHandle {
  readonly el: HTMLElement;
  /** Appelé dès que la ville distante est connue, et à chaque changement de nom. */
  setCity(city: RemoteCity | null): void;
  /**
   * Reflète le boost de visite en cours.
   *
   * FOURNI, jamais déduit : la valeur vient du serveur, seul à pouvoir la produire. Une
   * seconde source de vérité côté interface finirait par contredire celle qui crédite.
   * `until` permet le compte à rebours — « ×2 encore 1 h 12 » dit plus que « actif ».
   */
  setBoost(multiplier: number, until?: number): void;
  /**
   * Reflète l'état du jeu — visiteurs reçus et prochain palier de trace (§16.1).
   * Appelé à chaque changement de snapshot, comme les autres sections.
   */
  update(snapshot: CitySnapshot): void;
}

export function createSocialSection(options: SocialSectionOptions): SocialSectionHandle {
  const el = document.createElement('div');
  el.className = 'social-section';

  /* ── En attente d'adresse ────────────────────────────────────────────────── */
  const waiting = createEmptyState({
    icon: 'social',
    title: t('social.waitingTitle'),
    text: t('social.waitingText'),
  });

  /* ── Le lien ─────────────────────────────────────────────────────────────── */
  const head = document.createElement('h3');
  head.className = 'social__title';
  head.append(icon('social'), t('social.shareTitle'));

  const pitch = document.createElement('p');
  pitch.className = 'social__pitch';
  pitch.textContent = t('social.pitch');

  /*
    Le lien est un `<input readonly>` et non un `<span>`.

    Un texte ordinaire ne se sélectionne pas d'un geste sur téléphone, et le chemin
    `manual` de `shareCity` — contexte non sécurisé, presse-papiers refusé — n'aurait
    alors AUCUNE issue. Avec un champ, un tap sélectionne tout, et le joueur s'en sort
    même quand les deux API l'abandonnent.
  */
  const link = document.createElement('input');
  link.className = 'social__link';
  link.readOnly = true;
  link.spellcheck = false;
  link.setAttribute('aria-label', t('social.linkLabel'));
  link.addEventListener('focus', () => {
    link.select();
  });
  link.addEventListener('click', () => {
    link.select();
  });

  const shareButton: ButtonHandle = createButton({
    label: t('social.shareAction'),
    variant: 'primary',
    size: 'lg',
    icon: 'social',
    onClick: () => {
      void doShare();
    },
  });

  const hint = document.createElement('p');
  hint.className = 'social__hint';
  hint.textContent = t('social.hint');

  /*
    ── LA CONTREPARTIE, affichée là où on partage ─────────────────────────────

    Le §1 promet que chaque visite booste la production. Tant que rien ne le montrait,
    la promesse restait une phrase de page d'accueil : le joueur partageait son lien et
    n'avait aucun moyen de savoir si ça avait servi.

    Cette ligne est donc l'endroit exact où le partage rencontre sa récompense. Elle dit
    deux choses selon l'état, et jamais rien de vide : soit le boost en cours, soit
    l'invitation à partager. Un espace réservé qui attend une donnée serait précisément
    le « rien à viser » que le principe premier du §1 interdit.
  */
  const boost = document.createElement('p');
  boost.className = 'social__boost';
  boost.textContent = t('visits.boostNone');

  /*
    « Voir ma ville » — la vitrine (E§6) montrée à son propriétaire.

    Le joueur qui a VU sa page publique — heure dorée, trafic vivant — sait ce qu'il
    envoie, et un lien dont on est fier circule. Nouvel onglet, `noopener` : la page
    publique ne doit ni voler l'onglet du jeu ni recevoir une référence vers lui.
    L'auto-visite est déjà écartée côté serveur (slug mémorisé, contrainte en base).
  */
  const previewButton: ButtonHandle = createButton({
    label: t('social.preview'),
    variant: 'ghost',
    icon: 'eye',
    onClick: () => {
      if (current !== null)
        window.open(cityUrl(options.origin, current.slug), '_blank', 'noopener');
    },
  });
  previewButton.el.classList.add('social__preview');

  const sharePanel = createPanel({
    elevation: 'raised',
    children: [head, pitch, link, shareButton.el, previewButton.el, boost, hint],
  });

  /*
    ── Tes visiteurs — le panneau qui ferme la boucle du §9 ───────────────────

    Les traces (D.1) sont livrées sur la ville, mais rien ne disait au joueur CE QUI
    VIENT : « encore 7 visiteurs avant le banc » est l'objectif permanent que le §1
    réclame, et c'est lui qui transforme le partage en collection. Les seuils viennent
    de `config/balance.ts` — affichés, jamais recopiés (règle 22).
  */
  const visitorsHead = document.createElement('h3');
  visitorsHead.className = 'social__title';
  visitorsHead.append(icon('flower'), t('social.visitorsTitle'));

  const visitorsCount = document.createElement('p');
  visitorsCount.className = 'social__visitors';

  const tierLabel = document.createElement('p');
  tierLabel.className = 'social__tier';

  const tierProgress: ProgressHandle = createProgress({
    value: 0,
    max: 1,
    domain: 'social',
    label: t('social.visitorsTitle'),
  });

  const visitorsPanel = createPanel({
    children: [visitorsHead, visitorsCount, tierLabel, tierProgress.el],
  });
  visitorsPanel.el.classList.add('social__visitors-panel');

  /*
    ── Les canaux de Villopia (demande de Benoit, 18 août 2026) ───────────────
    Trois tuiles à LOGO — Discord, YouTube, TikTok — plutôt qu'un bouton texte :
    une marque se reconnaît à son signe avant de se lire. Les marques officielles
    sont dessinées en ligne (aucune requête externe, la CSP l'interdirait), chacune
    dans sa couleur d'origine. Liens sortants : nouvel onglet, `noopener`.
  */
  const channels = document.createElement('div');
  channels.className = 'social__channels';

  const CHANNELS: readonly {
    key: 'discord' | 'youtube' | 'tiktok';
    href: string;
    mark: string;
  }[] = [
    {
      key: 'discord',
      href: 'https://discord.gg/BTb5yD9mQ4',
      mark: `<svg viewBox="0 0 127.14 96.36" fill="#5865f2"><path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.9-72.15ZM42.45 65.69C36.18 65.69 31 60 31 53s5-12.74 11.43-12.74S54 46 53.89 53s-5.05 12.69-11.44 12.69Zm42.24 0C78.41 65.69 73.25 60 73.25 53s5-12.74 11.44-12.74S96.23 46 96.12 53s-5.04 12.69-11.43 12.69Z"/></svg>`,
    },
    {
      key: 'youtube',
      href: 'https://www.youtube.com/@villopia',
      mark: `<svg viewBox="0 0 576 512"><path fill="#f00" d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305z"/><path fill="#fff" d="M232.145 337.591V175.185l142.739 81.205z"/></svg>`,
    },
    {
      key: 'tiktok',
      href: 'https://www.tiktok.com/@ludopiagames',
      mark: `<svg viewBox="0 0 448 512" fill="currentColor"><path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.18V0l88 0a121.18 121.18 0 0 0 1.86 22.17 122.18 122.18 0 0 0 54 80.22 121.43 121.43 0 0 0 67 20.14Z"/></svg>`,
    },
  ];

  const countEls = new Map<'discord' | 'youtube' | 'tiktok', HTMLElement>();

  for (const channel of CHANNELS) {
    const link = document.createElement('a');
    link.className = 'social__channel';
    link.href = channel.href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.setAttribute('aria-label', t(`social.${channel.key}`));

    const mark = document.createElement('span');
    mark.className = 'social__channel-mark';
    mark.setAttribute('aria-hidden', 'true');
    mark.innerHTML = channel.mark;

    /*
      ── LA RÉCOMPENSE EST ANNONCÉE SUR LA TUILE ──────────────────────────────

      Une récompense qu'on découvre APRÈS avoir cliqué ne motive personne : elle remercie,
      elle n'invite pas. Le badge disparaît une fois le réseau récompensé — la tuile reste
      cliquable, parce qu'on peut vouloir y retourner sans être payé pour ça.
    */
    const claimed = options.claimedSocial?.() ?? [];
    const bounty = document.createElement('span');
    bounty.className = 'social__channel-bounty';
    bounty.textContent = `+${String(SOCIAL_REWARD_GEMS)} 💎`;
    bounty.hidden = claimed.includes(channel.key);
    link.append(bounty);

    link.addEventListener('click', () => {
      /*
        ⚠️ On n'empêche PAS la navigation, et on n'attend pas le retour du joueur. Le lien
        s'ouvre dans un onglet neuf : attendre qu'il revienne pour créditer, c'est ne
        jamais créditer sur mobile, où l'application est mise en veille.

        On ne sait de toute façon pas s'il a rejoint — seulement qu'il a ouvert. C'est ce
        que la récompense paie, et le libellé le dit.
      */
      const granted = options.onSocialVisit?.(channel.key) ?? 0;
      if (granted > 0) bounty.hidden = true;
    });

    const name = document.createElement('span');
    name.className = 'social__channel-name';
    name.textContent = t(`social.${channel.key}Name`);

    // Le compteur d'abonnés arrive par le réseau : caché tant qu'on ne SAIT pas —
    // « 0 abonné » sur un échec de lecture serait un mensonge triste.
    const count = document.createElement('span');
    count.className = 'social__channel-count';
    count.hidden = true;
    countEls.set(channel.key, count);

    link.append(mark, name, count);
    channels.append(link);
  }

  // Les compteurs se remplissent quand la réponse arrive ; un canal inconnu reste
  // une tuile sans nombre, jamais une erreur.
  void fetchSocialCounts().then((counts) => {
    if (counts === null) return;
    for (const [key, el] of countEls) {
      const value = counts[key];
      if (value === null) continue;
      el.textContent = t(key === 'discord' ? 'social.members' : 'social.followers', {
        value: n(value),
      });
      el.hidden = false;
    }
  });

  el.append(waiting.el, sharePanel.el, visitorsPanel.el, channels);

  let current: RemoteCity | null = null;
  let boostMultiplier = 1;
  let boostUntil = 0;

  /**
   * Rejouée par `setBoost` (le fait) et par `update` (le temps qui passe) : le compte
   * à rebours vit au rythme du jeu sans minuterie propre à désynchroniser.
   */
  function paintBoost(): void {
    const active = boostMultiplier > 1;
    const remaining = boostUntil - Date.now();
    boost.textContent = !active
      ? t('visits.boostNone')
      : remaining > 0
        ? t('visits.boostActiveFor', { value: n(boostMultiplier), time: formatClock(remaining) })
        : t('visits.boostActive', { value: n(boostMultiplier) });
    boost.classList.toggle('is-active', active);
  }

  async function doShare(): Promise<void> {
    if (current === null) return;
    const url = cityUrl(options.origin, current.slug);

    // `setLoading` pendant l'attente : la feuille native met un instant à s'ouvrir sur
    // téléphone, et un bouton inerte pendant ce temps se fait taper deux fois.
    shareButton.setLoading(true);
    const result = await shareCity({
      title: t('social.shareSubject', { name: current.name }),
      text: t('social.shareText', { name: current.name }),
      url,
    });
    shareButton.setLoading(false);

    // Le partage a EU LIEU : feuille native acceptée, ou lien copié. Un partage annulé
    // par le joueur ne compte pas — proposer un compte après un refus serait insister
    // sur ce qu'il vient de décliner.
    if (result.shared) options.onShared?.();

    if (result.method === 'clipboard' && result.shared) {
      options.onNotice(t('social.copied'));
      return;
    }
    if (result.method === 'manual') {
      // Aucun chemin automatique : on met le lien sous le doigt du joueur plutôt que de
      // lui annoncer un succès qui n'a pas eu lieu.
      link.focus();
      link.select();
      options.onNotice(t('social.selectManually'));
    }
  }

  function setCity(city: RemoteCity | null): void {
    current = city;
    const known = city !== null;
    waiting.el.hidden = known;
    sharePanel.el.hidden = !known;
    if (city !== null) link.value = cityUrl(options.origin, city.slug);
  }

  setCity(null);

  return {
    el,
    setCity,
    setBoost(multiplier, until = 0) {
      boostMultiplier = multiplier;
      boostUntil = until;
      paintBoost();
    },
    update(snapshot) {
      const visitors = Math.max(0, Math.floor(snapshot.visitorsReceived));
      visitorsCount.textContent = t('social.visitorsCount', { value: n(visitors) });

      /*
        Le prochain palier : d'abord les fleurs (une par visiteur jusqu'au plafond),
        puis les trophées de mobilier. Il y a TOUJOURS un objectif à montrer — après le
        dernier trophée, ce sont les jalons de visiteurs qui prennent le relais (§1).
      */
      const nextTier = VISITOR_DECORATIONS.tiers.find((tier) => visitors < tier.at);
      if (nextTier !== undefined) {
        const name = t(`social.tier.${nextTier.decoration}` as TranslationKey);
        tierLabel.textContent = t('social.tierNext', {
          value: n(nextTier.at - visitors),
          name,
        });
        const previous = [...VISITOR_DECORATIONS.tiers]
          .reverse()
          .find((tier) => tier.at <= visitors);
        const floor = previous?.at ?? 0;
        tierProgress.setValue((visitors - floor) / (nextTier.at - floor));
      } else {
        tierLabel.textContent = t('social.tierAll');
        tierProgress.setValue(1);
      }

      paintBoost();
    },
  };
}
